import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type { FileRecord } from "@/shared/domain/files";
import type { IFileService } from "@/shared/domain/files/FileService";
import {
  fileQueryKeys,
  patchCachedFileRecords,
} from "@/shared/domain/files/hooks";
import type { MetadataChangeset } from "@/shared/domain/metadataChangeset";
import { RobotoNotFoundException } from "@/shared/http";

import { canonicalAssetPath, canonicalBufferPath } from "../assetPaths";
import type { VisualizationAssetRequestResponse } from "../UrdfService";
import {
  isTerminalVisualizationAssetStatus,
  readVisualizationAsset,
  writeVisualizationAsset,
  VISUALIZATION_ASSET_METADATA_KEY,
  VISUALIZATION_ASSET_POLL_INTERVAL_MS,
  type VisualizationAssetRecord,
  type VisualizationAssetSettings,
} from "../visualizationAsset";

import { urdfQueryKeys } from "./urdfQueryKeys";

export interface RequestVisualizationAssetVariables {
  /** The source `.urdf`, which is what both the request and the path need. */
  file: Pick<FileRecord, "file_id" | "org_id" | "relative_path" | "upload_id">;
  settings: VisualizationAssetSettings;
}

/**
 * Kicks off a conversion of a robot description into a viewable 3D asset.
 *
 * The mutation resolves as soon as the platform has queued the work, not when
 * the artifact exists — {@link useVisualizationAsset} is what watches it
 * finish.
 *
 * Success seeds a `processing` block locally. That both moves a caller on
 * without waiting for the next poll and — more importantly — displaces any
 * *previous* run's block, which would otherwise still read `completed` or
 * `failed` and hold the wizard on the wrong step until the worker overwrites
 * it. Server-shaped, and the next poll replaces it with what the worker
 * actually wrote.
 */
export function useRequestVisualizationAsset() {
  const { urdf } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    VisualizationAssetRequestResponse,
    Error,
    RequestVisualizationAssetVariables
  >({
    mutationFn: ({ file, settings }) =>
      urdf.requestVisualizationAsset(file.file_id, settings, {
        resourceOwnerId: file.org_id,
      }),
    onSuccess: (response, { file }) => {
      const pending: VisualizationAssetRecord = {
        status: "processing",
        relative_path: response.relative_path,
        source_upload_id: file.upload_id,
      };
      queryClient.setQueryData(
        urdfQueryKeys.visualizationAsset(file.file_id),
        pending,
      );
      // The same block, written through to any cached search row for this file.
      // The picker that launched the wizard is still mounted behind it and is
      // reading the *list's* copy of this record, which would otherwise go on
      // saying the file has never been ingested for the whole of the run.
      patchCachedFileRecords(
        queryClient,
        { fileId: file.file_id, orgId: file.org_id },
        (record) => writeVisualizationAsset(record, pending),
      );
    },
  });
}

export interface UseVisualizationAssetOptions {
  /** Stop the query entirely — e.g. while the wizard's dialog is closed. */
  enabled?: boolean;
  /** Poll cadence while a conversion is in flight. */
  pollIntervalMs?: number;
}

/**
 * Watches a source file's conversion by re-reading the file's own record.
 *
 * Polling the record rather than listing the output directory is deliberate:
 * the generated glTF lives under a leading-dot path that both directory browse
 * and file search filter out, so the `roboto/visualization_asset` metadata
 * block is the only view of it that is not hidden.
 *
 * Polling **stops on a terminal status** — `completed` or `failed` are states
 * the worker will not move off, so continuing to poll would be pure load. A
 * record with no block at all keeps polling: that is the window between the
 * route accepting the request and the worker picking it up.
 *
 * Reaching a terminal status invalidates the file's detail query exactly (not
 * its subtree, which would include this one and re-trigger itself), so other
 * views of the record refresh once the artifact is real.
 *
 * @param file the **source** file — the `.urdf`, never the generated glTF
 * @returns the parsed status block, or `null` while the file has none
 */
export function useVisualizationAsset(
  file: Pick<FileRecord, "file_id" | "org_id"> | undefined,
  options: UseVisualizationAssetOptions = {},
): UseQueryResult<VisualizationAssetRecord | null, Error> {
  const { files } = useDomainServices();
  const queryClient = useQueryClient();
  const {
    enabled = true,
    pollIntervalMs = VISUALIZATION_ASSET_POLL_INTERVAL_MS,
  } = options;

  const fileId = file?.file_id;
  const orgId = file?.org_id;

  const query = useQuery<VisualizationAssetRecord | null, Error>({
    queryKey: urdfQueryKeys.visualizationAsset(fileId ?? ""),
    queryFn: async ({ signal }) => {
      if (fileId === undefined) {
        return Promise.reject(new Error("Invalid fileId"));
      }
      const record = await files.getFileRecord(fileId, {
        abortSignal: signal,
        resourceOwnerId: orgId,
      });
      return readVisualizationAsset(record);
    },
    enabled: enabled && fileId !== undefined,
    refetchInterval: (query) =>
      isTerminalVisualizationAssetStatus(query.state.data?.status)
        ? false
        : pollIntervalMs,
  });

  const block = query.data ?? null;
  const status = block?.status;
  const notified = React.useRef<string | undefined>(undefined);
  React.useEffect(
    function refreshRecordOnceConversionSettles() {
      if (fileId === undefined) {
        return;
      }
      if (block === null || !isTerminalVisualizationAssetStatus(status)) {
        // A run that has not finished is not one this has reported. Cleared
        // rather than left standing so that a *second* conversion of the same
        // file — after a reset, or a re-upload — is reported as readily as the
        // first was. The guard exists to stop a repeat within one run, not to
        // make a file reportable once per mount.
        notified.current = undefined;
        return;
      }
      const reached = `${fileId}:${status ?? ""}`;
      if (notified.current === reached) {
        return;
      }
      notified.current = reached;
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.detail(fileId),
        exact: true,
      });
      // Written, not invalidated. This block came back from the by-id record
      // route, so it is known to post-date the worker's write; the search that
      // caches the same record is served by an eventually-consistent replica
      // and could answer a refetch with the row as it was before the run. See
      // `patchCachedFileRecords`.
      if (orgId !== undefined) {
        patchCachedFileRecords(queryClient, { fileId, orgId }, (record) =>
          writeVisualizationAsset(record, block),
        );
      }
    },
    [fileId, orgId, block, status, queryClient],
  );

  return query;
}

/**
 * The fields resolving an artifact needs off its source record.
 *
 * `association_id` scopes the lookup to the dataset and `org_id` to the org;
 * `file_id` + `upload_id` + `relative_path` are what the canonical path is
 * derived from when the status block names none.
 */
export type VisualizationAssetSourceFile = Pick<
  FileRecord,
  "association_id" | "file_id" | "org_id" | "relative_path" | "upload_id"
>;

/**
 * The query that turns a source file into its generated artifact's record.
 *
 * Shared verbatim by {@link useVisualizationAssetFile} and
 * {@link useResolveVisualizationAssetFile} so the declarative and imperative
 * callers cannot drift — they hit the same cache entry, and a resolution made
 * on one path is already warm on the other.
 *
 * Resolved through the by-path **record** route rather than file search. The
 * artifact lives under `.VISUALIZATION_ASSETS/`, and search prefilters out every
 * node whose name starts with a dot, so a search for this path returns nothing
 * at all — the artifact is real but invisible to it. The record route applies no
 * such filter.
 *
 * The path is the block's `relative_path` when it has one, and otherwise the
 * path {@link canonicalAssetPath} derives from the source record. The fallback
 * is not a nicety: it is what lets a caller look for an existing artifact before
 * making any request at all, and it keeps a block that names no path from
 * stranding an artifact that does exist.
 */
function visualizationAssetFileQuery(
  files: Pick<IFileService, "getFileRecordByPath">,
  file: VisualizationAssetSourceFile | undefined,
  relativePath: string | undefined,
) {
  const datasetId = file?.association_id;
  const assetPath =
    relativePath ?? (file ? canonicalAssetPath(file) : undefined);

  return {
    locatable: datasetId !== undefined && assetPath !== undefined,
    queryKey: [
      ...fileQueryKeys.all(),
      "byRelativePath",
      datasetId ?? "",
      assetPath ?? "",
    ],
    queryFn: async ({
      signal,
    }: {
      signal?: AbortSignal;
    }): Promise<FileRecord | null> => {
      if (datasetId === undefined || assetPath === undefined) {
        return Promise.reject(new Error("Invalid asset location"));
      }
      try {
        // Keep the `await`: a 404 is raised while reading the response *body*,
        // so returning the promise bare would let the rejection escape this
        // `try` and propagate as a query error instead of a `null` result.
        return await files.getFileRecordByPath(datasetId, assetPath, {
          abortSignal: signal,
          resourceOwnerId: file?.org_id,
        });
      } catch (error) {
        if (error instanceof RobotoNotFoundException) {
          return null;
        }
        throw error;
      }
    },
  };
}

/**
 * Resolves the generated artifact's own `FileRecord`, given the source file.
 *
 * Every consumer downstream — the model loader above all — addresses files by
 * id, so the artifact has to be resolved from a path to a record.
 *
 * A file that genuinely is not there comes back as `null` rather than an error:
 * "the artifact does not exist" is a state the wizard renders, not a failure.
 *
 * @param file the **source** `.urdf`; the lookup is org-scoped and dataset-scoped,
 *   both of which come off this record
 * @param relativePath the path the status block names, if it names one
 * @param enabled skip the lookup entirely — e.g. before a conversion exists
 */
export function useVisualizationAssetFile(
  file: VisualizationAssetSourceFile | undefined,
  relativePath: string | undefined,
  enabled = true,
): UseQueryResult<FileRecord | null, Error> {
  const { files } = useDomainServices();
  const { locatable, queryKey, queryFn } = visualizationAssetFileQuery(
    files,
    file,
    relativePath,
  );

  return useQuery<FileRecord | null, Error>({
    queryKey,
    queryFn,
    enabled: enabled && locatable,
  });
}

/**
 * The same resolution as {@link useVisualizationAssetFile}, asked for once.
 *
 * A picker resolves the artifact at the moment a file is chosen — which is an
 * event, not a render — so the declarative form does not fit: there is no
 * `file` to hold in props until the click has already happened, and mounting a
 * query per listed row to be ready for it would be a record read per row.
 *
 * Goes through `fetchQuery`, so the result is cached under the same key the
 * hook reads and a second pick of the same file costs no request at all.
 *
 * @returns a resolver yielding the artifact's record, or `null` when the
 *   artifact does not exist
 */
export function useResolveVisualizationAssetFile(): (
  file: VisualizationAssetSourceFile,
  relativePath?: string,
) => Promise<FileRecord | null> {
  const { files } = useDomainServices();
  const queryClient = useQueryClient();

  return React.useCallback(
    (file: VisualizationAssetSourceFile, relativePath?: string) => {
      const { queryKey, queryFn } = visualizationAssetFileQuery(
        files,
        file,
        relativePath,
      );
      return queryClient.fetchQuery<FileRecord | null, Error>({
        queryKey,
        queryFn,
      });
    },
    [files, queryClient],
  );
}

/**
 * A glTF document, as far as deleting one needs to understand it.
 *
 * Only the buffer list, and only its `uri`s: the artifact's geometry lives in
 * sidecar files the document names, and those are the files a reset has to find.
 */
interface GltfBufferManifest {
  buffers?: { uri?: string }[];
}

export interface ResetVisualizationAssetVariables {
  /** The **source** `.urdf` whose generated asset is being destroyed. */
  file: VisualizationAssetSourceFile;
  /** The path the status block names, if it names one. */
  relativePath?: string;
}

/** What a reset actually destroyed, for a caller that wants to say so. */
export interface ResetVisualizationAssetResult {
  /** The ids of the files that were deleted — empty if there were none left. */
  deletedFileIds: string[];
}

/**
 * Destroys a source file's generated asset: the artifact, and the record of it.
 *
 * **This is not an unbind.** It deletes the generated `.gltf` and the sidecar
 * buffers it names, and then removes the `roboto/visualization_asset` block from
 * the source file so `ingestPhase` reports `settings` again and the wizard
 * offers a fresh conversion. The bytes are regenerable — by re-running a
 * conversion, which on a large robot is not instant — and nothing here is
 * undoable.
 *
 * ## The declared joint bindings go with it
 *
 * They are held in the artifact's own `ROBOTO_device` extension and nowhere
 * else — there is no longer a `roboto/joint_bindings` block on the source to
 * outlive it — so deleting the `.gltf` deletes them. Nothing here has to remove
 * them separately, and nothing re-applies them to the next conversion: they are
 * declared again against whatever it generates.
 *
 * A caller must say so in its confirmation. A destructive action that misstates
 * what it destroys is worse than one that says nothing, and a user who read
 * "your bindings are kept" would not re-declare a robot's worth of joints they
 * had in fact just lost.
 *
 * ## Order, and what each failure leaves behind
 *
 * The artifact goes first and the block second, so a failure to delete leaves
 * the block intact and the wizard still describing a preview that really does
 * exist. The wizard never claims there is no asset while its files are still
 * sitting in the dataset.
 *
 * The cost is the other window: deletes that succeed followed by a metadata
 * write that fails leave a block naming files that are gone, which reads as
 * `completed` and sends the preview hunting for a missing artifact. That state
 * is recoverable by pressing reset again rather than wedged, because resolution
 * treats an already-deleted file as nothing to delete — a second attempt skips
 * straight to the block.
 */
export function useResetVisualizationAsset() {
  const { files } = useDomainServices();
  const queryClient = useQueryClient();
  const resolveAssetFile = useResolveVisualizationAssetFile();

  return useMutation<
    ResetVisualizationAssetResult,
    Error,
    ResetVisualizationAssetVariables
  >({
    mutationFn: async ({ file, relativePath }) => {
      // Resolved fresh, never from cache. A reset re-run after a partial one
      // must see what is *now* there — a cached record of a file this mutation
      // already deleted would make the second attempt fail on a file that is
      // gone, which is the one thing that could wedge the recovery path.
      queryClient.removeQueries({
        queryKey: visualizationAssetFileQuery(files, file, relativePath)
          .queryKey,
      });
      const asset = await resolveAssetFile(file, relativePath);
      const doomed = [
        ...(asset ? [asset] : []),
        ...(await resolveSidecars(files, file, asset)),
      ];

      if (doomed.length > 0) {
        await files.deleteFiles(
          doomed.map((record) => record.file_id),
          { resourceOwnerId: file.org_id },
        );
      }

      // Removal, not an empty value: `readVisualizationAsset` reads whatever is
      // under the key, so writing `null` there would leave a block that parses
      // to nothing and a phase derived from a key that is still present.
      const changeset: MetadataChangeset = {
        remove_fields: [VISUALIZATION_ASSET_METADATA_KEY],
      };
      await files.putFileRecord(file.file_id, {
        body: JSON.stringify({ metadata_changeset: changeset }),
        resourceOwnerId: file.org_id,
      });

      return { deletedFileIds: doomed.map((record) => record.file_id) };
    },
    onSuccess: (_result, { file, relativePath }) => {
      // The wizard's phase is derived from this, so it is written rather than
      // invalidated: the user should be back on the settings step now, not after
      // a round trip.
      queryClient.setQueryData(
        urdfQueryKeys.visualizationAsset(file.file_id),
        null,
      );
      // Exact, so this does not cascade back down into the entry just written.
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.detail(file.file_id),
        exact: true,
      });
      // The by-path resolutions now point at files that no longer exist. Dropped
      // rather than invalidated, because the next conversion writes to the same
      // canonical path and a cached `null` would be as wrong as a cached record.
      queryClient.removeQueries({
        queryKey: visualizationAssetFileQuery(files, file, relativePath)
          .queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.lists(file.org_id),
      });
    },
  });
}

/**
 * The buffer files an artifact's document names, as records.
 *
 * Read off the document rather than derived, because the `.bin`'s name is
 * whatever `buffers[].uri` says it is — a bare filename resolved against the
 * artifact's own directory, which is the same sibling lookup the model loader
 * performs. Deleting a derived path instead would silently orphan the geometry
 * of any artifact whose buffer is not named canonically.
 *
 * Falls back to the canonical buffer path when the document cannot be read —
 * because it is unreadable, or because the `.gltf` is already gone from a reset
 * that got half way — so an artifact's geometry is not stranded by the loss of
 * the document that named it.
 *
 * A path that resolves to nothing is skipped: a buffer already gone is a buffer
 * that needs no deleting, which is also what makes a second reset attempt
 * possible after a partial one.
 */
async function resolveSidecars(
  files: Pick<IFileService, "getJsonFileContents" | "getFileRecordByPath">,
  source: VisualizationAssetSourceFile,
  asset: FileRecord | null,
): Promise<FileRecord[]> {
  const paths = asset
    ? await declaredBufferPaths(files, source, asset)
    : [canonicalBufferPath(source)];

  const resolved = await Promise.all(
    [...new Set(paths)]
      .filter((path) => path !== asset?.relative_path)
      .map(async (path) => {
        try {
          return await files.getFileRecordByPath(source.association_id, path, {
            resourceOwnerId: source.org_id,
          });
        } catch (error) {
          if (error instanceof RobotoNotFoundException) {
            return null;
          }
          throw error;
        }
      }),
  );
  return resolved.filter((record): record is FileRecord => record !== null);
}

/** The buffer paths an artifact's own document names, relative to the dataset. */
async function declaredBufferPaths(
  files: Pick<IFileService, "getJsonFileContents">,
  source: VisualizationAssetSourceFile,
  asset: FileRecord,
): Promise<string[]> {
  const directory = asset.relative_path.split("/").slice(0, -1).join("/");
  try {
    const document = await files.getJsonFileContents<GltfBufferManifest>(
      asset.file_id,
      { resourceOwnerId: source.org_id },
    );
    return (
      (document.buffers ?? [])
        .map((buffer) => buffer.uri)
        // A `data:` buffer is embedded in the document and has no file of its own.
        .filter(
          (uri): uri is string => uri !== undefined && !uri.startsWith("data:"),
        )
        .map((uri) => `${directory}/${decodeURIComponent(uri)}`)
    );
  } catch {
    return [canonicalBufferPath(source)];
  }
}
